<template>
  <div ref="root" class="sort-menu" :class="classModifiers">
    <sp-button
      ref="trigger"
      class="sort-menu__trigger"
      type="button"
      color="custom"
      :disabled="disabled"
      aria-haspopup="dialog"
      :aria-expanded="isDialogOpen"
      :data-testid="toggleAttribute"
      @click="handleTriggerClick"
    >
      <sp-icon slot="prepend" class="sort-menu__trigger-icon" name="sort-alt"></sp-icon>
      <span class="sort-menu__trigger-label"
        ><span v-if="triggerPrefix" class="sort-menu__trigger-prefix">{{ triggerPrefix }}:</span>
        {{ triggerLabel }}</span
      >
      <span v-if="caretImage" slot="append" class="sort-menu__trigger-caret --image" :style="caretImageStyle"></span>
      <sp-icon v-else slot="append" class="sort-menu__trigger-caret" name="chevron-down-filled"></sp-icon>
    </sp-button>

    <dialog
      :id="dialogId"
      ref="dialog"
      class="sort-menu__dialog"
      :aria-labelledby="dialogTitleId"
      @cancel="handleDialogCancel"
      @close="handleDialogClose"
      @keydown.esc="handleDialogEscape"
      @click="handleDialogClick"
    >
      <div class="sort-menu__group">
        <h2 :id="dialogTitleId" class="sort-menu__dialog-title">{{ title }}</h2>
        <div class="sort-menu__options">
          <label
            v-for="option in options"
            :key="option.value"
            class="sort-menu__option"
            v-bind="optionAttributes(option)"
          >
            <input
              :checked="draft === option.value"
              class="sort-menu__option-input"
              type="radio"
              :name="optionInputName"
              :value="option.value"
              @input.stop
              @change="handleOptionChange"
            />
            <span class="sort-menu__option-copy">
              <span class="sort-menu__option-label">{{ option.label }}</span>
              <span v-if="option.hint" class="sort-menu__option-hint">{{ option.hint }}</span>
            </span>
          </label>
        </div>
      </div>
      <div class="sort-menu__actions">
        <sp-button class="sort-menu__done-button" type="button" color="primary" @click="applyAndClose">
          {{ closeText }}
        </sp-button>
      </div>
    </dialog>
  </div>
</template>

<script setup>
import { useEventListener, useMutationObserver } from "@vueuse/core";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useAriaControls } from "#ce/composables/useAriaControls";
import { useBreakpoints } from "#ce/composables/useBreakpoints";

const emit = defineEmits(["select", "apply", "close"]);

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  draft: {
    type: String,
    default: "",
  },
  selectId: {
    type: String,
    default: "",
  },
  triggerLabel: {
    type: String,
    default: "",
  },
  triggerPrefix: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Sort results by",
  },
  closeText: {
    type: String,
    default: "Done",
  },
  focusRing: {
    type: Boolean,
    default: false,
  },
  anchorHost: {
    type: Boolean,
    default: false,
  },
  autoHeight: {
    type: Boolean,
    default: false,
  },
  caretImage: {
    type: String,
    default: "",
  },
  toggleTestId: {
    type: String,
    default: "",
  },
  optionTestId: {
    type: String,
    default: "",
  },
});

const root = useTemplateRef("root");
const trigger = useTemplateRef("trigger");
const dialog = useTemplateRef("dialog");
const isDialogOpen = ref(false);
const lastFocusedElement = ref(null);
// True for the rest of a click that began inside the dialog, so the clicks page code re-dispatches from ancestors in it are not outside clicks.
let isInsideClick = false;
let isReopening = false;

const host = computed(() => root.value?.getRootNode()?.host);
const { isSmScreen } = useBreakpoints();

const classModifiers = computed(() => {
  const modifiers = [];

  if (isSmScreen.value) {
    modifiers.push("--is-sm-screen");
  }

  if (props.autoHeight) {
    modifiers.push("--auto-height");
  }

  return modifiers;
});
const caretImageStyle = computed(() => ({ "--sort-menu-caret-image": `url("${props.caretImage}")` }));
const toggleAttribute = computed(() => props.toggleTestId || undefined);

function optionAttributes(option) {
  if (!props.optionTestId) {
    return {};
  }

  return { "data-testid": props.optionTestId, "sort-label-name": option.value };
}

const idSuffix = computed(() => props.selectId || host.value?.id || "sort");
const optionInputName = computed(() => `sort-option-${idSuffix.value}`);
const dialogId = computed(() => `sort-dialog-${idSuffix.value}`);
const dialogTitleId = computed(() => `${dialogId.value}-title`);
useAriaControls(trigger, dialog);

useEventListener(document, "click", markInsideClick, { capture: true });
useEventListener(document, "click", handleDocumentClick);

// AJAX detach removes the dialog from the top layer (drops ::backdrop). The
// open attribute persists, which makes showModal throw — clear it first, then
// re-add to the top layer. Reactive target detaches the observer when the
// sheet isn't open or we're on desktop, so it doesn't tax unrelated DOM churn.
const backdropObserverTarget = computed(() => (isSmScreen.value && isDialogOpen.value ? document.body : null));

function restoreTopLayer() {
  if (!dialog.value) {
    return;
  }

  let isAlreadyModal = false;

  try {
    isAlreadyModal = dialog.value.matches(":modal");
  } catch {
    // jsdom doesn't support :modal
  }

  if (isAlreadyModal) {
    return;
  }

  try {
    dialog.value.removeAttribute("open");
    dialog.value.showModal();
  } catch {
    // Transient state; next mutation retries.
  }
}

useMutationObserver(backdropObserverTarget, restoreTopLayer, { childList: true, subtree: true });

function closeWhenDisabled(isDisabled) {
  if (isDisabled) {
    closeDialog({ restoreFocus: false });
  }
}

watch(() => props.disabled, closeWhenDisabled);

function reopenForScreenChange() {
  if (!dialog.value?.open) {
    return;
  }

  isReopening = true;
  dialog.value.close();
  showDialog();
}

watch(isSmScreen, reopenForScreenChange);

function showDialog() {
  if (!dialog.value) {
    return;
  }

  if (dialog.value.open) {
    return;
  }

  if (isSmScreen.value) {
    dialog.value.showModal();
  } else {
    dialog.value.show();
    anchorDialogToHost();
  }

  isDialogOpen.value = true;
  requestAnimationFrame(focusActiveOption);
}

function anchorDialogToHost() {
  if (!props.anchorHost || !host.value) {
    return;
  }

  dialog.value.style.setProperty("--sort-menu-anchor-height", `${host.value.getBoundingClientRect().height}px`);
}

function handleTriggerClick(event) {
  if (props.disabled) {
    return;
  }

  lastFocusedElement.value = event?.currentTarget ?? document.activeElement;

  if (dialog.value?.open) {
    closeDialog();
  } else {
    showDialog();
  }
}

function closeDialog({ restoreFocus = true } = {}) {
  if (!dialog.value || !dialog.value.open) {
    return;
  }

  if (!restoreFocus) {
    lastFocusedElement.value = null;
  }

  dialog.value.close();
}

function applyAndClose() {
  emit("apply");
  closeDialog();
}

function handleDialogClick(event) {
  if (event.target !== dialog.value) {
    return;
  }

  const { left, right, top, bottom } = dialog.value.getBoundingClientRect();
  const isInsideDialog =
    event.clientX >= left && event.clientX <= right && event.clientY >= top && event.clientY <= bottom;

  if (!isInsideDialog) {
    closeDialog();
  }
}

function handleDialogCancel(event) {
  event.preventDefault();
  closeDialog();
}

function handleDialogClose() {
  if (isReopening) {
    isReopening = false;
    return;
  }

  isDialogOpen.value = false;
  emit("close");
  restoreFocusAfterClose();
}

function handleDialogEscape() {
  closeDialog();
}

function markInsideClick(event) {
  if (eventOriginatedInsideComponent(event)) {
    isInsideClick = true;
    setTimeout(clearInsideClick);
  }
}

function clearInsideClick() {
  isInsideClick = false;
}

function handleDocumentClick(event) {
  if (!dialog.value?.open || isSmScreen.value || isInsideClick) {
    return;
  }

  if (!eventOriginatedInsideComponent(event)) {
    closeDialog({ restoreFocus: false });
  }
}

function eventOriginatedInsideComponent(event) {
  const path = event.composed ? event.composedPath() : null;
  const clickedInsideInnerContent = path ? path.includes(root.value) : root.value?.contains(event.target);
  const clickedInsideCustomElementHost = path
    ? Boolean(host.value && path.includes(host.value))
    : Boolean(host.value?.contains(event.target));

  return clickedInsideInnerContent || clickedInsideCustomElementHost;
}

function handleOptionChange(event) {
  emit("select", event.target.value);
}

function focusActiveOption() {
  if (!dialog.value?.open) {
    return;
  }

  const selectedOptionInput = dialog.value.querySelector("input.sort-menu__option-input:checked");
  const fallbackOptionInput = dialog.value.querySelector("input.sort-menu__option-input");
  const focusTarget = selectedOptionInput || fallbackOptionInput || dialog.value;

  if (props.focusRing) {
    focusTarget?.blur();
    focusTarget?.focus({ focusVisible: true });
  } else {
    focusTarget?.focus();
  }
}

function restoreFocusAfterClose() {
  const focusTarget = lastFocusedElement.value;
  if (focusTarget?.isConnected && typeof focusTarget.focus === "function") {
    focusTarget.focus();
  }

  lastFocusedElement.value = null;
}

defineExpose({
  close: closeDialog,
});
</script>

<style lang="scss" scoped>
.sort-menu {
  &__trigger {
    vertical-align: var(--sp-comp-sort-trigger-vertical-align, baseline);
    --sp-comp-button-custom-bg-color: transparent;
    --sp-comp-button-custom-text-color: var(--sp-comp-sort-trigger-text-color, var(--sp-sys-color-primary));
    --sp-comp-button-custom-hover-text-color: var(--sp-comp-sort-trigger-text-color, var(--sp-sys-color-primary));
    --sp-comp-button-border-color: var(--sp-comp-sort-trigger-border-color, var(--sp-sys-color-outline-variant));
    --sp-comp-button-border-width: 1px;
    --sp-comp-button-border-radius: var(--sp-comp-sort-trigger-border-radius, var(--sp-sys-shape-control));
    --sp-comp-button-min-width: 0;
    --sp-comp-button-size: var(--sp-comp-sort-trigger-height, auto);
    --sp-comp-button-compact-height: var(--sp-comp-sort-trigger-height, var(--sp-sys-form-field-compact-min-height));
    --sp-comp-button-gap: var(--sp-comp-sort-trigger-gap, var(--sp-ref-scale-6));
    --sp-comp-button-padding: var(--sp-comp-sort-trigger-padding, var(--sp-ref-scale-6) var(--sp-ref-scale-16));
    --sp-comp-button-font-short: var(--sp-comp-sort-trigger-font, var(--sp-sys-typescale-body-semi-bold-short));
    --sp-comp-button-prepend-icon-size: var(--sp-comp-sort-trigger-icon-size, var(--sp-sys-icon-size-x-small));
    --sp-comp-button-prepend-icon-fill: var(
      --sp-comp-sort-trigger-icon-color,
      var(--sp-comp-sort-trigger-text-color, var(--sp-sys-color-primary))
    );
    --sp-comp-button-append-icon-size: var(--sp-comp-sort-trigger-icon-size, var(--sp-sys-icon-size-x-small));
    --sp-comp-button-append-icon-fill: var(
      --sp-comp-sort-trigger-icon-color,
      var(--sp-comp-sort-trigger-text-color, var(--sp-sys-color-primary))
    );

    cursor: pointer;
    margin: 0;
    white-space: nowrap;
  }

  &__dialog {
    background-color: var(--sp-comp-sort-dialog-background-color, canvas);
    border: var(--sp-comp-sort-dialog-border, var(--sp-sys-form-field-border));
    border-radius: var(--sp-comp-sort-dialog-border-radius, var(--sp-sys-shape-control));
    box-shadow: var(--sp-comp-sort-dialog-box-shadow, var(--sp-ref-shadow-2));
    box-sizing: border-box;
    bottom: auto;
    left: var(--sp-comp-sort-dialog-left, 0);
    margin: 0;
    overflow-y: visible;
    padding: var(--sp-comp-sort-dialog-padding, var(--sp-ref-scale-20) var(--sp-ref-scale-24) var(--sp-ref-scale-40));
    position: absolute;
    right: var(--sp-comp-sort-dialog-right, auto);
    top: calc(var(--sort-menu-anchor-height, 100%) + var(--sp-comp-sort-dialog-offset, var(--sp-ref-scale-4)));
    width: var(--sp-comp-sort-dialog-width, 20rem);
    z-index: var(--sp-sys-z-index-max);
  }

  &__trigger-caret.--image {
    background-color: var(
      --sp-comp-sort-trigger-icon-color,
      var(--sp-comp-sort-trigger-text-color, var(--sp-sys-color-primary))
    );
    display: inline-block;
    height: var(--sp-comp-sort-trigger-icon-size, var(--sp-sys-icon-size-x-small));
    mask: var(--sort-menu-caret-image) center / contain no-repeat;
    min-height: var(--sp-comp-sort-trigger-icon-size, var(--sp-sys-icon-size-x-small));
    min-width: var(--sp-comp-sort-trigger-icon-size, var(--sp-sys-icon-size-x-small));
    width: var(--sp-comp-sort-trigger-icon-size, var(--sp-sys-icon-size-x-small));
  }

  &__trigger-label {
    display: var(--sp-comp-sort-trigger-label-display, inline);
    font-family: var(--sp-comp-sort-trigger-font-family, inherit);
    font-weight: var(--sp-comp-sort-trigger-font-weight, inherit);
    gap: var(--sp-comp-sort-trigger-label-gap, normal);
    line-height: var(--sp-comp-sort-trigger-line-height, inherit);
    white-space: nowrap;
  }

  &__trigger-prefix {
    display: var(--sp-comp-sort-trigger-prefix-display, inline);
  }

  &__dialog-title {
    color: var(--sp-comp-sort-dialog-title-color, var(--sp-ref-palette-text));
    display: block;
    font: var(--sp-comp-sort-dialog-title-font, var(--sp-sys-typescale-body-bold-short));
    margin: 0 0 var(--sp-comp-sort-dialog-title-margin, var(--sp-ref-scale-14));
  }

  &__group {
    align-items: var(--sp-comp-sort-group-align-items, normal);
    display: var(--sp-comp-sort-group-display, block);
    flex-wrap: var(--sp-comp-sort-group-flex-wrap, nowrap);
  }

  &__options {
    display: contents;
  }

  &__option {
    color: var(--sp-comp-sort-option-color, var(--sp-ref-palette-text));
    font: var(--sp-comp-sort-option-font, inherit);
    cursor: pointer;
    display: grid;
    column-gap: 0.5ch;
    row-gap: 0;
    grid-template-columns: auto 1fr;

    &:not(:last-child) {
      margin-bottom: var(--sp-comp-sort-option-gap, var(--sp-ref-scale-10));
    }
  }

  &__option-input {
    accent-color: var(--sp-sys-color-primary);
    align-self: center;
    outline: var(--sp-comp-sort-option-input-outline, revert);
    margin: var(--sp-comp-sort-option-input-margin, 0);
  }

  &__option-copy {
    display: contents;
  }

  &__option-label {
    font: var(--sp-sys-typescale-body-semi-bold-short);
  }

  &__option-hint {
    color: inherit;
    font: var(--sp-comp-sort-option-hint-font, var(--sp-sys-typescale-body-small-short));
    grid-column: 2;
  }

  &__actions {
    display: none;
  }

  &__done-button {
    --sp-comp-button-width: 100%;
    --sp-comp-button-min-width: 0;
    --sp-comp-button-min-height: var(--sp-comp-sort-done-min-height, var(--sp-comp-button-size));
    --sp-comp-button-border-radius: var(--sp-comp-sort-done-button-border-radius, var(--sp-sys-shape-control));
  }

  &.--auto-height .sort-menu__trigger {
    --sp-comp-button-size: auto;
    --sp-comp-button-compact-height: auto;
  }

  &.--is-sm-screen {
    .sort-menu__group {
      display: block;
    }

    .sort-menu__trigger {
      --sp-comp-button-border-width: 0;
      --sp-comp-button-gap: 0;
      --sp-comp-button-padding: var(--sp-comp-sort-trigger-sm-padding, 0);
    }

    .sort-menu__dialog {
      border: 0;
      border-radius: var(--sp-ref-scale-8) var(--sp-ref-scale-8) 0 0;
      box-shadow: var(--sp-comp-sort-dialog-sm-box-shadow, none);
      inset: auto 0 0 0;
      max-width: 100vw;
      max-height: 85vh;
      overflow-y: auto;
      padding: var(--sp-comp-sort-dialog-sm-padding, var(--sp-ref-scale-16) var(--sp-ref-scale-16) 0);
      position: fixed;
      width: 100vw;

      &::backdrop {
        background-color: var(--sp-comp-sort-backdrop-background-color, #000);
        opacity: var(--sp-comp-sort-backdrop-opacity, 0.3);
      }
    }

    .sort-menu__actions {
      display: block;
      margin-top: var(--sp-comp-sort-actions-margin-top, var(--sp-ref-scale-16));
      padding: var(--sp-comp-sort-actions-padding, var(--sp-ref-scale-14) 0);
    }
  }
}
</style>
